import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useScreenSize } from '@composables/useScreenSize';

// Check if running in iframe mode (opened from website widget)
function checkIframeMode() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('iframeMode') === '1';
}

export const useAuthStore = defineStore('auth', () => {
  const bearer = ref('');
  const apikey = ref('');
  const channelKey = ref('');
  /** True when the user is in the agent view; mobile channel key is only used in classic BE. */
  const isAgentView = ref(false);

  // Function to validate channel key for mobile devices (classic booking engine only)
  const validateChannelKey = (key) => {
    const websiteChannelKey = '58e5de4e971fc00be29aa10492813ad4';
    const mobileChannelKey = '974d82150b02b308a6a79ba2f87acad6';

    // In agent view, never substitute mobile; keep the key as-is (e.g. agent channel key).
    if (isAgentView.value) {
      return key ?? '';
    }

    // In iframe mode, always use website channel (ignore mobile channel even if passed)
    if (checkIframeMode()) {
      if (key && key !== mobileChannelKey) {
        return key;
      }
      return websiteChannelKey;
    }

    const { isMobile } = useScreenSize();

    // Only in classic BE: on mobile, use mobile channel when key is empty or website
    if (isMobile.value && (!key || key === websiteChannelKey)) {
      return mobileChannelKey;
    }

    return key ?? '';
  };

  const setAgentView = (value) => {
    isAgentView.value = !!value;
  };

  const setToken = (token) => {
    bearer.value = token;
  };

  const updateToken = (token) => {
    bearer.value = token;
  };

  const setApiKey = (key) => {
    apikey.value = key;
  };

  const setChannelKey = (key) => {
    // Validate and potentially update the channel key for store value
    const validatedKey = validateChannelKey(key);
    channelKey.value = validatedKey;
  };

  //action to set the api key and channel key
  const setApiKeyAndChannelKey = (key, channel) => {
    apikey.value = key;
    // Validate and potentially update the channel key for store value
    const validatedChannel = validateChannelKey(channel ?? '');
    channelKey.value = validatedChannel;
  };

  const clearToken = () => {
    bearer.value = '';
  };

  return {
    bearer,
    apikey,
    channelKey,
    isAgentView,
    setToken,
    updateToken,
    setApiKey,
    setChannelKey,
    setApiKeyAndChannelKey,
    setAgentView,
    clearToken,
  };
});
